import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Note as NoteIcon } from "@phosphor-icons/react";
import { money } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";

export function NotesBadge({ count, testid }) {
  if (!count) return null;
  return (
    <span data-testid={testid} title={`${count} note${count === 1 ? "" : "s"}`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-muted-foreground bg-secondary border border-border rounded-full px-1.5 py-0.5 leading-none">
      <NoteIcon size={10} weight="fill" /> {count}
    </span>
  );
}

export function useFocusHighlight() {
  const [sp] = useSearchParams();
  const focusId = sp.get("focus");
  const ref = useRef(null);
  useEffect(() => {
    if (focusId && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusId]);
  const rowProps = (id) =>
    id && id === focusId
      ? { ref, className: "ring-2 ring-primary ring-inset bg-primary/5 transition-shadow duration-300" }
      : {};
  return { focusId, rowProps };
}

export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel = "Confirm", onConfirm, testid = "confirm-dialog", children, confirmDisabled }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="confirm-cancel">Cancel</Button>
          <Button disabled={confirmDisabled} onClick={() => { onConfirm(); onOpenChange(false); }} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="confirm-accept">{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function KpiCard({ label, value, sub, accent, testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      data-testid={testid}
      onClick={onClick}
      className={`border border-border bg-card p-4 sm:p-5 text-left w-full hover:border-primary/40 transition-colors duration-150 ${onClick ? "cursor-pointer hover:bg-secondary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" : ""}`}
    >
      <p className="overline-label">{label}</p>
      <p className={`text-2xl sm:text-3xl font-black tracking-tight mt-2 tabular ${accent || ""}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Tag>
  );
}

const STATUS_STYLES = {
  Paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Open: "bg-blue-50 text-blue-700 border-blue-200",
  Partial: "bg-amber-50 text-amber-700 border-amber-200",
  Invoiced: "bg-violet-50 text-violet-700 border-violet-200",
  Draft: "bg-zinc-100 text-zinc-600 border-zinc-200",
  Received: "bg-emerald-50 text-emerald-700 border-emerald-200",
  posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  draft: "bg-zinc-100 text-zinc-600 border-zinc-200",
  Low: "bg-red-50 text-red-700 border-red-200",
};

export function StatusChip({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm ${
        STATUS_STYLES[status] || "bg-zinc-100 text-zinc-600 border-zinc-200"
      }`}
    >
      {status}
    </span>
  );
}

export function SectionHeader({ title, subtitle, children }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
      <div>
        <h2 className="text-xl font-black tracking-tight">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export function Money({ value, className = "" }) {
  return <span className={`tabular ${className}`}>{money(value)}</span>;
}
